// r24 — Single-FAQ deep page renderer (used by /translate/naati/faq/$slug)
// Zero client JS, JSON-LD-ready via head(). Uniqueness sourced from NAATI_FAQS.
import { Link } from "@tanstack/react-router";
import HubLeadCTA from "@/components/HubLeadCTA";
import NaatiAuthorByline from "@/components/NaatiAuthorByline";
import NaatiTripleKeyLinks from "@/components/NaatiTripleKeyLinks";
import NaatiSchemaGraph from "@/components/NaatiSchemaGraph";
import type { NaatiFaq } from "@/data/naati-faq-bank";
import { NAATI_FAQS } from "@/data/naati-faq-bank";

export interface NaatiFaqRendererProps {
  locale: "th" | "en";
  faq: NaatiFaq;
}

export default function NaatiFaqRenderer({ locale, faq }: NaatiFaqRendererProps) {
  const isTh = locale === "th";
  const home = isTh ? "/translate/naati" : "/en/translate/naati";
  const faqHome = isTh ? "/translate/naati/faq" : "/en/translate/naati/faq";
  const q = isTh ? faq.questionTh : faq.questionEn;
  const shortA = isTh ? faq.shortAnswerTh : faq.shortAnswerEn;
  const longA = isTh ? faq.longAnswerTh : faq.longAnswerEn;
  const related = faq.relatedSlugs
    .map((s) => NAATI_FAQS.find((f) => f.slug === s))
    .filter(Boolean) as NaatiFaq[];

  const paragraphs = longA.split(/(?<=[.!?ฯ])\s+(?=[A-Z(ก-ฮ])/g);

  const path = isTh ? `/translate/naati/faq/${faq.slug}` : `/en/translate/naati/faq/${faq.slug}`;

  return (
    <main className="mx-auto max-w-3xl px-5 py-12 sm:px-8 sm:py-16">
      <NaatiSchemaGraph
        locale={locale}
        path={path}
        title={q}
        description={shortA}
        crumbs={[
          { name: isTh ? "หน้าแรก" : "Home", path: isTh ? "/" : "/en" },
          { name: "NAATI", path: home },
          { name: "FAQ", path: faqHome },
          { name: q, path },
        ]}
      />
      <nav className="mb-6 text-xs text-slate-500" aria-label="Breadcrumb">
        <Link to="/" className="hover:text-amber-700">{isTh ? "หน้าแรก" : "Home"}</Link>
        <span className="mx-2">/</span>
        <Link to={home as any} className="hover:text-amber-700">NAATI</Link>
        <span className="mx-2">/</span>
        <Link to={faqHome as any} className="hover:text-amber-700">FAQ</Link>
      </nav>

      <div className="text-[11px] font-bold uppercase tracking-widest text-amber-700" data-speakable="true">
        {isTh ? "คำถามยอดฮิตเกี่ยวกับ NAATI" : "NAATI Frequently Asked Question"}
      </div>
      <h1 className="mt-2 font-display text-3xl font-extrabold text-navy-deep sm:text-4xl" data-speakable="true">
        {q}
      </h1>

      <section className="mt-6 rounded-2xl border border-amber-200 bg-amber-50/60 p-5">
        <div className="text-[11px] font-bold uppercase tracking-widest text-amber-700">
          {isTh ? "คำตอบสั้น" : "Direct Answer"}
        </div>
        <p className="mt-2 text-base font-medium text-navy-deep direct-answer" data-speakable="true">
          {shortA}
        </p>
      </section>

      <section className="mt-10">
        <h2 className="font-display text-2xl font-extrabold text-navy-deep">
          {isTh ? "คำอธิบายเชิงลึก" : "In-depth Answer"}
        </h2>
        {paragraphs.map((para, i) => (
          <p key={i} className="mt-4 text-base leading-relaxed text-slate-700">
            {para}
          </p>
        ))}
      </section>

      {related.length > 0 && (
        <section className="mt-12">
          <h2 className="font-display text-xl font-extrabold text-navy-deep">
            {isTh ? "อ่านต่อในเรื่องที่เกี่ยวข้อง" : "Related questions"}
          </h2>
          <ul className="mt-4 space-y-2 text-sm">
            {related.map((r) => (
              <li key={r.slug}>
                <Link
                  to={(isTh ? `/translate/naati/faq/${r.slug}` : `/en/translate/naati/faq/${r.slug}`) as any}
                  className="text-amber-700 underline-offset-2 hover:underline"
                >
                  {isTh ? r.questionTh : r.questionEn}
                </Link>
              </li>
            ))}
          </ul>
        </section>
      )}

      <NaatiAuthorByline lang={isTh ? "th" : "en"} />

      <NaatiTripleKeyLinks
        mode={isTh ? "th" : "en"}
        countryNameTh="ออสเตรเลีย"
        countryNameEn="Australia"
      />

      <HubLeadCTA
        locale={isTh ? "th" : "en"}
        service="NAATI Translation"
        title={isTh ? "มีเอกสารต้องแปล NAATI แล้ว?" : "Ready to order a NAATI translation?"}
      />
    </main>
  );
}
